import { Injectable, OnDestroy } from '@angular/core';
// @ts-ignore
import { LngLatLike, LngLat } from '!mapbox-gl';
import { BBOX } from '../types/@types/sl-v-types';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { BufferSrc } from '../model/buffersrc';
import { SLLayer } from '../model/sl-layer';
import { Synchronizer, SynchEvent, ISelectionInfo } from '../model/synchronizer';
import { UUID } from 'angular2-uuid';
import { ResultHistory, MomentChange } from '../model/result-history';
import { Grouping } from 'angular-slickgrid';
import { Dict } from '../types/decorable';
import { AggColumnIds } from '../model/aggregators/aggregator-set';

export type ResultContext = 'search' | 'query' | 'query-vis' |
  'roi-query' | 'roi-query-vis' | 'query-by-feature' | 'query-by-feature-vis' |
  'gps-query' | 'gps-query-vis' | 'sel-add' | 'sel-remove' | 'ro-sel-add' | 'roi-sel-remove' |
  'result-remove' | 'results-list-selection' | 'result-popup-selection' | 'result-state-change';

export type ResultsSelectionMode = 'single' | 'multi';

export interface ResultDisplayStates {
  [layerId: string]: ResultDisplayState
}
export interface ResultDisplayState {
  columnIds: Array<string>;
  frozenColumnId: -1 | number;
  anchorColumn?: string;
  groupingIds: Array<string>;
  aggregators: AggColumnIds;
  filter?: string;
  showFilters?: boolean;
  showGroupings?: boolean;
  sorting: Array<{ columnId: string, sortAsc: boolean }>;
  columnFilters?: Array<{ columnId: string; operator: string; searchTerms: Array<any>; }>
}



export interface ContextState {
  context: ResultContext;
  layerId: string | number;
  layer: SLLayer;
  features: Array<string | number>;
  unfeatures: Array<string | number>;
  channel: string | number;
}

export interface ResultSet {
  results: Array<ResultSetSrc>;
  centroid?: LngLat;
  featureCount?: number;
  displayedFeatureCount?: number;
  layerCount?: number;
  displayedLayerCount?: number;
  action: string;
  status: string;
  status_message?: string;
  displayedLayers?: {
    [layerId: number]: boolean;
  }
}
export interface ResultMoment {
  id: string;
  name: string;
  description: string,
  resultSet: ResultSet;
  when: Date;
  context: ResultContext;
  bufferSrc?: BufferSrc;
  params: any;
  endpoint: string;
  synchs: any;
  displayStates: ResultDisplayStates;
}
export interface ResultSetSrc {
  id?: string | number;
  name?: string;
  layer_name: string;
  layer: string | number;
  plid?: string | number;
  fields: Array<string>;
  features: Array<any>;
  centroid: LngLatLike;
  layer_bbox?: BBOX;
  paged_bbox?: BBOX;
  metadata?: any;
  addedGids?: Array<string | number>;
  removedGids?: Array<string | number>;
  activeGids?: Array<string | number>;
  inactiveGids?: Array<string | number>;
  display?: boolean;
}
export interface ResultChange {
  service: ResultsService;
  change?: string;
}


@Injectable({
  providedIn: 'root'
})
export class ResultsService extends Synchronizer implements OnDestroy {
  lastTooltip: ResultSet = null;
  currentMoment: ResultMoment = null;
  resultSet: ResultSet = null;
  currentLayer: ResultSetSrc;
  activeGids: Array<string | number>;
  activeFeatures: Array<any>;
  synchronizing = false;
  synchOffset = -1;
  history: ResultHistory;
  subscriptions: Array<Subscription> = [];

  // tslint:disable-next-line:variable-name
  private _resultChanges: BehaviorSubject<ResultChange> = new BehaviorSubject({ service: this });
  public readonly resultChanges: Observable<ResultChange> = this._resultChanges.asObservable();

  // tslint:disable-next-line:variable-name
  private _tooltipChanges: BehaviorSubject<ResultSet | null> = new BehaviorSubject(this.lastTooltip);
  public readonly tooltipChanges: Observable<ResultSet | null> = this._tooltipChanges.asObservable();


  constructor() {
    super();
    this.history = new ResultHistory();
    this.subscriptions.push(this.history.momentChange.subscribe((event: MomentChange) => {
      this.Synch(
        {
          channel: 'history',
          data: event,
          source: 'history',
          topic: 'moment-change'
        }
      )
    }));
  }
  SetTooltip(resultSet: Array<ResultSet>) {
    if (resultSet.length === 0) {
      this.lastTooltip = null;
    } else {
      this.lastTooltip = resultSet[0];
    }
    this._tooltipChanges.next(this.lastTooltip);
  }

  CompareSelections(selA: ISelectionInfo, selB: ISelectionInfo) {
    if (!selA && !selB) {
      return true;
    }
    if (!selA || !selB) {
      return false;
    }
    const layerMatch = selA.layerId === selB.layerId;
    let selMatch = false;
    if (selA.features === selB.features === undefined) {
      selMatch = true;
    } else if (!!selA.features && !!selB.features) {
      selMatch = (selA.features.length === selB.features.length &&
        selA.features.every((value, index) => value === selB.features[index]));
    } else if (!!selA.features || !!selB.features) {
      selMatch = false;
    }
    let unSelMatch = false;
    if (selA.unfeatures) {
      selA.unfeatures.sort();;
    }
    if (selB.unfeatures) {
      selB.unfeatures.sort();
    }
    if (selA.unfeatures === selB.unfeatures === undefined) {
      unSelMatch = true;
    } else if (!!selA.unfeatures && !!selB.unfeatures) {
      unSelMatch = (selA.unfeatures.length === selB.unfeatures.length &&
        selA.unfeatures.every((value, index) => value === selB.unfeatures[index]));
    } else if (!!selA.unfeatures || !!selB.unfeatures) {
      unSelMatch = false;
    }
    return layerMatch && selMatch && unSelMatch;
  }


  SetResults(
    resultSet: ResultSet,
    context: ResultContext,
    params: any,
    endpoint: string,
    bufferSrc?: BufferSrc,
    synchronize: boolean = false,
    channel: number = ResultHistory.LATEST_ID
  ) {
    let featureCount = 0;
    let layerCount = 0;
    for (const result of resultSet.results) {
      layerCount += 1;
      featureCount += result.features.length;
    }

    resultSet.layerCount = layerCount;
    resultSet.featureCount = featureCount;
    resultSet.displayedFeatureCount = featureCount;
    resultSet.displayedLayerCount = layerCount;
    const moment: ResultMoment = {
      id: UUID.UUID(),
      name: 'Last Spatial Search Result Set',
      description: '',
      resultSet: (resultSet),
      context: (context),
      params: (params),
      endpoint: (endpoint),
      when: new Date(),
      synchs: this.history.GetDefaultSynchSet(),
      displayStates: {}
    };
    if (!!bufferSrc) {
      moment.bufferSrc = bufferSrc;
    }
    if (channel === ResultHistory.LATEST_ID) {
      this.history.latest = moment;
    } else {
      this.history.UpsertMoment(moment, channel);
    }

    const synchEvent: SynchEvent = {
      channel: (channel),
      data: moment,
      source: context,
      topic: 'set-results'
    }
    if (synchronize) {
      this.Synch(synchEvent);
    }
    this._resultChanges.next({ service: this, change: 'results/results-set' });
  }
  SetTabularResults(
    resultSet: ResultSet,
    context: ResultContext,
    params: any,
    endpoint: string,
    bufferSrc?: BufferSrc,
    synchronize: boolean = false,
    channel: number = ResultHistory.LATEST_TABULAR_ID
  ) {
    let featureCount = 0;
    let layerCount = 0;
    for (const result of resultSet.results) {
      layerCount += 1;
      featureCount += result.features.length;
    }

    resultSet.layerCount = layerCount;
    resultSet.featureCount = featureCount;
    resultSet.displayedFeatureCount = featureCount;
    resultSet.displayedLayerCount = layerCount;
    const moment: ResultMoment = {
      id: UUID.UUID(),
      name: 'Last Tabular Search Result Set',
      description: '',
      resultSet: (resultSet),
      context: (context),
      params: (params),
      endpoint: (endpoint),
      when: new Date(),
      synchs: this.history.GetDefaultSynchSet(),
      displayStates: {}
    };
    if (!!bufferSrc) {
      moment.bufferSrc = bufferSrc;
    }
    if (channel === ResultHistory.LATEST_TABULAR_ID) {
      this.history.latestTabular = moment;
    } else {
      this.history.UpsertMoment(moment, channel);
    }

    const synchEvent: SynchEvent = {
      channel: (channel),
      data: moment,
      source: context,
      topic: 'set-results'
    }
    if (synchronize) {
      this.Synch(synchEvent);
    }
    this._resultChanges.next({ service: this, change: 'results/results-set' });
  }
  SetActiveResultLayer(layerId: number | string, plid?: number | string) {
    const current = this.currentLayer
    if (!!this.history.lastMoment) {
      this.currentMoment = this.history.lastMoment;
      this.resultSet = this.currentMoment.resultSet;

      for (const resultSrc of this.resultSet.results) {
        if (!!plid) {
          if (!!resultSrc.plid) {
            if ('' + resultSrc.plid === ('' + plid)) {
              this.currentLayer = resultSrc;
              break;
            }
            continue;
          } else {
            if (resultSrc.layer + '' === (layerId + '')) {
              this.currentLayer = resultSrc;
              break;
            }
            continue;
          }
        } else {
          if (resultSrc.layer + '' === (layerId + '')) {
            this.currentLayer = resultSrc;
            break;
          }
          continue;
        }

      }
    }
    if (this.currentLayer !== current) {
      this._resultChanges.next({ service: this, change: 'results/active_layer/set' });
    }
    return;
  }
  InitGidSets() {
    if (!(!!this.currentLayer)) {
      return;
    }
    const resultSrc = this.currentLayer;
    if (!(!!resultSrc.activeGids)) {
      resultSrc.activeGids = [];
    }
    if (!(!!resultSrc.inactiveGids)) {
      resultSrc.inactiveGids = [];
      for (const feature of resultSrc.features) {
        resultSrc.inactiveGids.push(feature.gid);
      }
    }

  }
  GetGids(layerId: string | number, plid: string | number): Array<string | number> {
    this.SetActiveResultLayer(layerId, plid);
    if (!this.currentLayer) {
      return [];
    }
    const gids = [];
    if (!!this.currentLayer.features) {
      for (const result of this.currentLayer.features) {
        gids.push(result.gid);
      }
    }
    return gids;
  }
  SelectResults(layer: string | number, plid?: string | number, gids?: Array<string | number>) {
    plid = plid;
    this.SetActiveResultLayer(layer);
    this.currentLayer.activeGids = [];
    this.currentLayer.inactiveGids = [];
    this.activeFeatures = [];
    if (!!gids) {
      this.activeGids = gids;
      for (const feature of this.currentLayer.features) {
        if (feature.gid === feature.gid) {
          this.currentLayer.activeGids.push(feature.gid);
        } else {
          this.currentLayer.inactiveGids.push(feature.gid);
        }
      }
    }
    this._resultChanges.next({ service: this, change: 'results/selected' });

  }
  SelectAll() {
    this.activeGids = [];
    this.activeFeatures = [];
    this.activeFeatures = this.currentLayer.features;
    this.currentLayer.activeGids = [];
    this.currentLayer.inactiveGids = [];
    for (const feature of this.currentLayer.features) {
      this.currentLayer.activeGids.push(feature.gid);
    }
    this._resultChanges.next({ service: this, change: 'results/selected' });
  }
  UpdateResultsListSelections(state: ContextState) {
    this.SetActiveResultLayer(state.layer.id, state.layer.plid);
    if (!!this.currentLayer) {
      this.currentLayer.activeGids = state.features;
      this.currentLayer.inactiveGids = state.unfeatures;
    }
    this._resultChanges.next({ service: this, change: 'results/selected' });
  }
  Synchronize(layer: string | number, plid: string | number, offset: number) {
    this.SetActiveResultLayer(layer, plid);
    this.activeGids = [];
    this.activeFeatures = [];
    this.activeGids.push(this.currentLayer.features[offset].gid);
    this.activeFeatures.push(this.currentLayer.features[offset]);
    this.synchronizing = true;
    this.synchOffset = offset;
    // this._synchChanges.next({ service: this, change: 'results/synch/start' });
    this._resultChanges.next({ service: this, change: 'results/selected' });
  }
  SynchOffset(offset: number) {
    this.activeGids = [];
    this.activeFeatures = [];
    this.activeGids.push(this.currentLayer.features[offset].gid);
    this.activeFeatures.push(this.currentLayer.features[offset]);
    this.synchOffset = offset;
  }
  SynchHasNext() {
    if ((this.currentLayer.features.length - 1) === this.synchOffset) {
      return false;
    }
    return true;
  }
  SynchHasPrev() {
    return this.synchOffset > 0;
  }
  SynchNext(): void | false {
    if (this.SynchHasNext() === false) {
      return false;
    }
    this.SynchOffset(this.synchOffset + 1);
    // this._synchChanges.next({ service: this, change: 'results/synch/next' });
  }
  SynchPrev(): void | false {
    if (this.SynchHasPrev() === false) {
      return false;
    }
    this.SynchOffset(this.synchOffset - 1);
    // this._synchChanges.next({ service: this, change: 'results/synch/prev' });
  }
  SynchFirst(): void {
    this.SynchOffset(0);
    // this._synchChanges.next({ service: this, change: 'results/synch/first' });
  }
  SynchLast(): void {
    this.SynchOffset(this.currentLayer.features.length - 1);
    // this._synchChanges.next({ service: this, change: 'results/synch/last' });
  }
  DeSynch(): void {
    this.synchronizing = false;
    // this._synchChanges.next({ service: this, change: 'results/synch/stop' });
  }
  HasResults(): boolean {
    if (!(!!this.history.latest)) {
      return false;
    }
    if (this.history.latest.resultSet === null) {
      return false;
    }
    if (this.history.latest.resultSet.results.length === 0) {
      return false;
    }
    if (this.currentLayer === null) {
      return false;
    }
    if (this.currentLayer.features.length === 0) {
      return false;
    }
    return true;
  }
  UpdateFeatures(newFeatures: Array<any>, layer?: number | string, plid?: number | string, gids?: Array<string | number>): void | false {
    if (!!layer || !!plid) {
      if (!!plid) {
        if (this.currentLayer.plid !== plid) {
          return false;
        }
      } else if (!!layer) {
        if (this.currentLayer.layer !== layer) {
          return false;
        }
      }
    }
    this.currentLayer.features = newFeatures;
    this._resultChanges.next({ service: this, change: 'results/features/change' });
    if (!!layer || !!plid) {
      if (!!gids) {
        this.SelectResults(layer, plid, gids);
      }
    }
  }
  UpdateMoment(mergeParams: any = {}, layer?: SLLayer) {
    if (!!this.currentMoment) {
      this.history.UpdateMoment(this.currentMoment, false, mergeParams);
    }
  }
  ngOnDestroy() {
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
  }
  HasLatestSpatialMoment(): boolean {
    return !!this.history.GetMoment(ResultHistory.LATEST_ID);
  }
  GetLatestSpatialMoment():ResultMoment | null {
    return this.history.GetMoment(ResultHistory.LATEST_ID);
  }
  HasLatestTabularMoment():boolean {
    return !!this.history.GetMoment(ResultHistory.LATEST_TABULAR_ID);
  }
  GetLatestTabularMoment():ResultMoment | null {
    return this.history.GetMoment(ResultHistory.LATEST_TABULAR_ID);
  }
}
